/**
 * Impersonation bookkeeping for analytics.
 *
 * The hazard this exists to solve: `loginAsUser` / `loginAsMaker` overwrite the
 * `auth_token` cookie with the TARGET's token and then navigate (or, for
 * makers, `window.location.reload()`). The SPA reboots, `getUser` returns the
 * target's profile, and naive instrumentation would `identify(targetId)` —
 * silently attributing an admin's clicks to a maker, poisoning that maker's
 * People profile, and making "makers who create orders" a lie.
 *
 * So before the token is swapped we record who the real human is. The analytics
 * plugin reads this at boot, keeps the ADMIN's distinct_id, and blocks profile
 * writes for the rest of the session.
 *
 * localStorage rather than sessionStorage: it has to survive
 * `window.location.reload()` and a new tab.
 */

export const IMPERSONATION_KEY = "and_dine_impersonation"

/** A stale record must not mislabel a normal session forever. */
export const IMPERSONATION_TTL_MS = 12 * 60 * 60 * 1000

export type ImpersonationKind = "user" | "maker"

export type ImpersonationRecord = {
	/** Past the TTL. Still honoured — only a confirmed token swap clears it. */
	is_stale?: boolean
	impersonator_user_id: string
	impersonation_kind: ImpersonationKind
	impersonated_user_id?: string
	/** Maker.id is a UUID (DataTypes.UUID); `number` here rejected every real id. */
	impersonated_maker_id?: string | number
	started_at: number
}

export const startImpersonation = (
	record: Omit<ImpersonationRecord, "started_at">
): ImpersonationRecord | null => {
	if (!import.meta.client || !record?.impersonator_user_id) return null

	const stored: ImpersonationRecord = { ...record, started_at: Date.now() }
	try {
		localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(stored))
	} catch {
		return null
	}
	return stored
}

export const clearImpersonation = () => {
	if (!import.meta.client) return
	try {
		localStorage.removeItem(IMPERSONATION_KEY)
	} catch {
		/* no-op */
	}
}

/**
 * Returns null — and clears the key — for anything missing or malformed.
 *
 * An AGED record is deliberately NOT cleared: it comes back with `is_stale`
 * and is still honoured, because a long impersonation still holds the target's
 * token and dropping the guard would let the next boot write their profile.
 */
export const readImpersonation = (): ImpersonationRecord | null => {
	if (!import.meta.client) return null

	let raw: string | null = null
	try {
		raw = localStorage.getItem(IMPERSONATION_KEY)
	} catch {
		return null
	}
	if (!raw) return null

	let parsed: ImpersonationRecord
	try {
		parsed = JSON.parse(raw)
	} catch {
		clearImpersonation()
		return null
	}

	if (!parsed?.impersonator_user_id || typeof parsed.started_at !== "number") {
		clearImpersonation()
		return null
	}

	// Deliberately NOT expiring on age alone. A maker impersonation that runs
	// past the TTL still holds the target's token, so clearing here would let the
	// next reload identify the target and write their People profile — the exact
	// thing this record prevents. Age only downgrades to "stale": only an explicit session
	// transition (for example logout or a fresh login) can clear it safely.
	if (Date.now() - parsed.started_at > IMPERSONATION_TTL_MS) {
		return { ...parsed, is_stale: true }
	}

	return parsed
}
